<template>
  <Transition name="modal">
    <div v-if="show" class="modal-mask" @click="closeHandler()">
      <div class="modal-wrapper">
        <div :class="containerClass">
          <div v-if="showHeader" class="modal-confirm-header">
            <slot name="header"></slot>
          </div>
          <div class="modal-confirm-body" :class="bodyAdditionalClass">
            <slot name="body"></slot>
          </div>
          <div
            :class="
              showCancelButton
                ? 'modal-confirm-footer'
                : 'modal-confirm-footer-alt'
            "
          >
            <button
              v-if="showCancelButton"
              id="cancel"
              class="modal-button btn btn-outline-success"
              :class="{ 'fs-3 pj-ss-btn': isSelfService }"
              @click="cancelHandler()"
            >
              <slot name="cancel"> {{ t("SHARED.CANCEL") }} </slot>
            </button>
            <button
              v-if="showConfirmButton"
              id="confirm"
              class="modal-button btn"
              :class="[
                confirmButtonDisabled ? 'btn-disabled' : 'btn-success',
                { 'fs-3 pj-ss-btn': isSelfService },
              ]"
              @click="confirmHandler()"
              :disabled="confirmButtonDisabled"
            >
              <i v-show="showSpinner" class="fa fa-spin fa-spinner"></i>
              <slot name="confirm"> {{ t("SHARED.CONFIRM") }} </slot>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  show: { type: Boolean, default: false },
  size: { type: String, default: "" },
  showCancelButton: { type: Boolean, default: true },
  showConfirmButton: { type: Boolean, default: true },
  isSelfService: { type: Boolean, default: false },
  disableConfirmButton: { type: Boolean, default: false },
  bodyAdditionalClass: { type: String, default: "" },
  showHeader: { type: Boolean, default: true },
});

const emit = defineEmits(["cancelSelected", "confirmSelected", "closeModal"]);

const { t } = useI18n();
const confirmButtonDisabled = ref(props.disableConfirmButton);
const showSpinner = ref(false);

const containerClass = computed(() => {
  let className = "modal-confirm-container";

  //If you need to add another custom modal size,
  // pass it via props, add it to the switch statement here,
  switch (props.size) {
    case "medium":
      className = "modal-confirm-medium-container";
      break;
    case "large":
      className = "modal-confirm-large-container";
      break;
    case "xl":
      className = "modal-confirm-xl-container";
      break;
  }

  return className;
});
const confirmCompleted = () => {
  confirmButtonDisabled.value = false;
  showSpinner.value = false;
};

const cancelHandler = () => {
  emit("cancelSelected", true);
};

const confirmHandler = () => {
  confirmButtonDisabled.value = true;
  showSpinner.value = true;
  emit("confirmSelected", true, confirmCompleted);
};

const closeHandler = () => {
  emit("closeModal", true);
};

watch(
  () => props.disableConfirmButton,
  (disableConfirmButton) => {
    confirmButtonDisabled.value = disableConfirmButton;
  }
);
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.modal-mask {
  position: fixed;
  z-index: 9988;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  display: table;
  transition: opacity 0.3s ease;
}
.modal-mask .closer {
  display: table-cell;
  vertical-align: middle;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-confirm-container {
  width: 500px;
  min-height: 256px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  @media screen and (max-width: $mobile) {
    width: 354px;
  }
}
.modal-confirm-medium-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 20px 26px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.modal-confirm-large-container {
  max-width: 760px;
  margin: 0 auto;
  padding: 20px 26px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.modal-confirm-xl-container {
  max-width: 1000px;
  max-height: 800px;
  overflow-y: auto;
  margin: 0 auto;
  padding: 20px 26px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
}

.modal-confirm-header {
  border-bottom: 1px solid #ddd;
  padding: 10px 20px;
  align-items: center;
  color: black;
  font-style: normal;
  font-weight: 700;
  font-size: 21px;
  line-height: 33px;
}

.modal-confirm-body {
  width: 100%;
  padding: 20px;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  left: 0;
  right: 0;
  color: #000;
}

.modal-confirm-footer {
  display: flex;
  padding: 20px;
  justify-content: space-between;
}

.modal-confirm-footer-alt {
  text-align: right;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-confirm-container,
.modal-leave-to .modal-confirm-container {
  transform: translateY(-20px);
  opacity: 0;
}

.modal-button {
  border-radius: 8px;
}

.btn-success {
  margin-top: 15px;
  color: white;
  background-color: $pj-color-green;
  border-color: $pj-color-green;
  width: 161px;
  height: 48px;
}

.btn-success:hover {
  background-color: $pj-color-gray;
  border-color: $pj-color-gray;
}

.btn-disabled {
  margin-top: 15px;
  color: white;
  width: 161px;
  height: 48px;
  background-color: $pj-color-gray;
  border-color: $pj-color-gray;
}

.btn-outline-success {
  margin-top: 17px;
  color: $pj-color-green;
  border-color: $pj-color-green;
  width: 131px;
  height: 44px;
}

.btn-outline-success:hover {
  color: white;
  background-color: $pj-color-gray;
  border-color: $pj-color-gray;
}

.btn-success:focus,
.btn-outline-success:focus {
  box-shadow: none;
}
.align-end {
  align-self: flex-end;
}
</style>
